import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Card, CardContent, Fade, Grow, Typography } from '@mui/material';
import { ssoApi } from 'app/services/api';
import storage, { sessionStorage } from 'app/services/storage';
import AppVersionInline from 'app/shared/components/AppVersionInline';
import BackgroundCarousel from 'app/shared/components/common/BackgroundCarousel';
import SplashScreen from 'app/shared/components/common/SplashScreen';
import BrandingFooter from 'app/shared/componentsRestyled/common/BrandingFooter';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import { LOGIN_METHOD, PASSKEY_ENROLLMENT_PROMPT_FLAG, TPO_INVITE_CLIENT_ID } from 'constants/auth';
import { LS_IS_HEADER_COLLAPSED } from 'constants/localStorage';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getDomainName } from 'utils/domain';
import { openSsoInBrowser } from 'utils/native/ssoBrowser';
import { getIsNativePlatform } from 'utils/platform';

import { fetchSignInMethodAction, submitOtpSignInAction } from 'store/auth/actions';
import { selectTenantBranding, selectTenantName } from 'store/auth/selectors';
import { showErrorMessageAction } from 'store/global/actions';

import LoginLogo from './components/LoginLogo';
import {
  COMPACT_LOGO_MAX_HEIGHT,
  DEFAULT_LOGIN_HEADER,
  KEYBOARD_OPEN_SELECTOR,
  SPACING_LG,
  SPACING_SM,
  SPACING_XS,
  USE_PASSWORD_TO_SIGN_IN_MESSAGE,
} from './Login.constants';
import { LoginTestIds } from './Login.testIds';
import { isUsePasswordToSignInError, parseActivationParamsFromSearch } from './Login.utils';
import JWTLoginTab from './tabs/JWTLoginTab';
import OtpLoginTab from './tabs/OtpLoginTab';
import PasskeyLoginTab from './tabs/PasskeyLoginTab';
import SetInitialPasswordTab from './tabs/SetInitialPasswordTab';

const rootStyles = {
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  color: ({ palette }) => palette.primary.contrastText,
};

// Outer scroll container. When the viewport shrinks (e.g. the native software keyboard opening
// over the login form) the card is kept reachable by scrolling instead of being clipped.
// `paddingTop: env(safe-area-inset-top)` keeps the card clear of the Dynamic Island / status bar
// on native — the login page renders pre-auth, outside the authed layout that applies the inset
// elsewhere, so it must reserve the inset itself (0px on web). `border-box` keeps the padding
// inside the fixed top:0/bottom:0 box instead of overflowing the viewport.
const contentWrapperStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  boxSizing: 'border-box',
  paddingTop: 'env(safe-area-inset-top, 0px)',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  zIndex: 1,
};

// Inner centering wrapper. `minHeight: 100%` (not `height`) lets it grow taller than the scroll
// container when the form does not fit, so `justifyContent: center` centers the card when there is
// room but never clips it on overflow — the outer container then scrolls over the full content.
const contentCenterStyles = {
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  padding: ({ spacing }) => spacing(SPACING_XS),
  // Keyboard open: stop centering and pull the card up to the top so all the reclaimed vertical
  // space goes below the form and the password + Login button stay visible above the keyboard. The
  // safe-area (Dynamic Island) clearance is applied by the outer scroll container's paddingTop, so
  // flex-start still lands below the island, not under it.
  [KEYBOARD_OPEN_SELECTOR]: {
    justifyContent: 'flex-start',
  },
};

const footerWrapperStyles = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  // The footer is pinned to the bottom of the viewport; when the keyboard shrinks the WebView it
  // would overlay the login form, so hide it while the user is signing in on a short viewport.
  [KEYBOARD_OPEN_SELECTOR]: {
    display: 'none',
  },
};

const loginCardStyles = {
  width: '100%',
  maxWidth: 400,
  borderRadius: ({ shape }) => shape.borderRadius,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  overflow: 'hidden',
  marginTop: ({ spacing }) => spacing(SPACING_SM),
  // Drop the top margin when the keyboard is open so the card sits flush under the safe area.
  [KEYBOARD_OPEN_SELECTOR]: {
    marginTop: 0,
  },
};

const loginCardContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: ({ spacing }) => spacing(SPACING_SM),
  '@media (min-width: 768px)': {
    padding: ({ spacing }) => spacing(SPACING_LG),
  },
  [KEYBOARD_OPEN_SELECTOR]: {
    padding: ({ spacing }) => spacing(SPACING_XS),
  },
};

const logoStyles = {
  maxHeight: 128,
  maxWidth: '100%',
  mb: SPACING_SM,
  filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2))',
  // Shrink the oversized logo on a short viewport so the whole form fits above the keyboard.
  [KEYBOARD_OPEN_SELECTOR]: {
    maxHeight: COMPACT_LOGO_MAX_HEIGHT,
    mb: SPACING_XS,
  },
};

const loginHeaderStyles = {
  marginBottom: ({ spacing }) => spacing(SPACING_SM),
  fontWeight: 500,
  textAlign: 'center',
  width: '100%',
  color: ({ palette }) => palette.text.primary,
  [KEYBOARD_OPEN_SELECTOR]: {
    marginBottom: ({ spacing }) => spacing(SPACING_XS),
  },
};

const Login = ({ setIsLoading, isLoading }) => {
  const dispatch = useDispatch();
  const [signInMethod, setSignInMethod] = useState(null);
  const [isSignInMethodResolved, setIsSignInMethodResolved] = useState(false);
  const [ssoUrl, setSsoUrl] = useState(null);
  const [otpRequestId, setOtpRequestId] = useState(null);
  const [isAwaitingOtp, setIsAwaitingOtp] = useState(false);
  const [passwordSignInNotice, setPasswordSignInNotice] = useState(null);
  const [isEmailCodeSignIn, setIsEmailCodeSignIn] = useState(false);
  const [isPasskeyFallback, setIsPasskeyFallback] = useState(false);
  // DEV-4157: holds the in-memory activation token when the OTP confirm returns a constrained
  // activation-only response. Non-null = the set-password step is shown instead of the app shell.
  const [activationToken, setActivationToken] = useState(null);

  // DEV-4179: guard so the activation-link auto-dispatch fires exactly once on mount.
  const activationFiredRef = useRef(false);

  const tenantName = useSelector(selectTenantName);
  const tenantBranding = useSelector(selectTenantBranding);

  const loginHeader = tenantName || DEFAULT_LOGIN_HEADER;

  useEffectSafeStrictMode(() => {
    const fetchMethod = async () => {
      try {
        setIsLoading(true);
        const methodData = await dispatch(fetchSignInMethodAction());
        const method = methodData?.method ?? null;

        setSignInMethod(method);

        if (methodData?.ssoUrl) {
          setSsoUrl(methodData.ssoUrl);
        }

        if (!methodData?.ssoUrl && method === LOGIN_METHOD.SSO) {
          try {
            const { data: url } = await ssoApi.getSsoUrlByDomainname({
              domainname: getDomainName(),
            });

            setSsoUrl(url);
          } catch {
            // SSO URL fetch failure is non-fatal; the button will be shown without a URL
          }
        }
      } catch {
        setSignInMethod(null);
      } finally {
        setIsSignInMethodResolved(true);
        setIsLoading(false);
      }
    };

    fetchMethod();
  }, [dispatch, setIsLoading]);

  useEffect(() => {
    storage.setItem(LS_IS_HEADER_COLLAPSED, true);
  }, []);

  // DEV-4179: When an invited passwordless TPO user clicks their activation email link, they land
  // on this login page with ?requestId=<id>&code=<code> in the URL. Auto-drive the existing OTP
  // confirm → set-password path so they don't have to interact with the login form at all.
  //
  // The ref guard ensures this fires exactly once per mount regardless of React Strict Mode's
  // double-invoke. After the dispatch we do not need to clear the URL params — the user will
  // navigate away when the set-password step completes via completeSignIn.
  useEffect(() => {
    if (activationFiredRef.current) {
      return;
    }

    const activationParams = parseActivationParamsFromSearch(window.location.search);

    if (!activationParams) {
      return;
    }

    activationFiredRef.current = true;

    const runActivation = async () => {
      try {
        const result = await dispatch(
          submitOtpSignInAction({
            requestId: activationParams.requestId,
            code: activationParams.code,
            // The SignInRequest behind an emailed activation link is always created with this
            // ClientId (InviteSubCompanyUserCommandHandler), not ADMIN_PANEL_CLIENT_ID.
            clientId: TPO_INVITE_CLIENT_ID,
          }),
        );

        if (result?.requiresPasswordSetup) {
          setActivationToken(result.activationToken);
        }
        // If the company is Otp-based or the invitee already had a password,
        // submitOtpSignInAction calls completeSignIn internally and the authed
        // shell renders — no further action needed here.
      } catch (activationError) {
        if (isUsePasswordToSignInError(activationError)) {
          setPasswordSignInNotice(USE_PASSWORD_TO_SIGN_IN_MESSAGE);

          return;
        }

        dispatch(
          showErrorMessageAction({ message: 'The activation link is invalid or has expired.' }),
        );
      }
    };

    runActivation();
  }, [dispatch]);

  const onSsoLoginClick = useCallback(() => {
    if (!ssoUrl) {
      return;
    }

    if (getIsNativePlatform()) {
      openSsoInBrowser(ssoUrl);

      return;
    }

    window.location.href = ssoUrl;
  }, [ssoUrl]);

  const onSecondFactorRequired = useCallback(requestId => {
    setOtpRequestId(requestId);
    setIsAwaitingOtp(true);
  }, []);

  const onPasswordSignInRequired = useCallback(message => {
    setPasswordSignInNotice(message);
    setIsEmailCodeSignIn(false);
    setIsAwaitingOtp(false);
    setOtpRequestId(null);
  }, []);

  const onEmailCodeRequested = useCallback(
    requestId => {
      setPasswordSignInNotice(null);
      setIsEmailCodeSignIn(true);
      onSecondFactorRequired(requestId);
    },
    [onSecondFactorRequired],
  );

  const onBackToPassword = useCallback(() => {
    setIsEmailCodeSignIn(false);
    setIsAwaitingOtp(false);
    setOtpRequestId(null);
  }, []);

  const onFallbackToPassword = useCallback(() => {
    // PasskeyWithUsername + no/declined passkey → fall back to the username + password form, and flag this
    // session so the authed shell prompts a one-time passkey enrollment after sign-in (DEV-3881
    // enroll-on-first-login).
    sessionStorage.setItem(PASSKEY_ENROLLMENT_PROMPT_FLAG, true);
    setIsPasskeyFallback(true);
  }, []);

  // DEV-4157: called by OtpLoginTab when the OTP confirm returns an activation-only token.
  const onActivationRequired = useCallback(({ activationToken: token }) => {
    setActivationToken(token);
  }, []);

  const renderLoginContent = () => {
    // DEV-4157: activation step takes precedence — the user must set a password before anything else.
    if (activationToken) {
      return (
        <SetInitialPasswordTab activationToken={activationToken} signInMethod={signInMethod} />
      );
    }

    if (isAwaitingOtp && otpRequestId) {
      const showRememberDevice = signInMethod === LOGIN_METHOD.PASSWORD_PLUS_OTP_REMEMBER_DEVICE;

      return (
        <OtpLoginTab
          showRememberDevice={showRememberDevice}
          signInRequestId={otpRequestId}
          onActivationRequired={onActivationRequired}
          onBack={isEmailCodeSignIn ? onBackToPassword : null}
          onPasswordSignInRequired={onPasswordSignInRequired}
        />
      );
    }

    switch (signInMethod) {
      case LOGIN_METHOD.SSO:
        return (
          <>
            <Button
              aria-label="LOG IN"
              className="w-full mx-auto mt-16 normal-case"
              color="primary"
              data-testid={LoginTestIds.LOGIN_BUTTON}
              disabled={!ssoUrl}
              type="button"
              variant="contained"
              onClick={onSsoLoginClick}
            >
              SSO Login
            </Button>

            {!ssoUrl && (
              <Typography className="mt-16 text-center text-red" variant="medium14">
                SSO is not configured for this tenant. Contact your administrator.
              </Typography>
            )}
          </>
        );

      case LOGIN_METHOD.OTP:
        return <JWTLoginTab isOtpOnly onSecondFactorRequired={onSecondFactorRequired} />;

      case LOGIN_METHOD.PASSWORD_PLUS_OTP:
      case LOGIN_METHOD.PASSWORD_PLUS_OTP_REMEMBER_DEVICE:
        return (
          <JWTLoginTab
            notice={passwordSignInNotice}
            onEmailCodeRequested={onEmailCodeRequested}
            onSecondFactorRequired={onSecondFactorRequired}
          />
        );

      case LOGIN_METHOD.PASSKEY_WITH_USERNAME:
        if (isPasskeyFallback) {
          return <JWTLoginTab />;
        }

        return <PasskeyLoginTab onFallbackToPassword={onFallbackToPassword} />;

      case LOGIN_METHOD.PASSWORD:
        return (
          <JWTLoginTab notice={passwordSignInNotice} onEmailCodeRequested={onEmailCodeRequested} />
        );

      default:
        return <JWTLoginTab />;
    }
  };

  // Also hold the splash until the sign-in method has resolved ONCE. withLoading starts isLoading at
  // false, so the form used to paint for a frame before the fetch flipped it on — the boot then read
  // as login -> logo -> login (DEV-5584). Staying on the splash until we know which method to show
  // also stops the password tab flashing up before an SSO/passkey tenant's real form replaces it.
  if (isLoading || !isSignInMethodResolved) {
    return <SplashScreen />;
  }

  return (
    <Box sx={rootStyles}>
      <BackgroundCarousel />

      <Box sx={contentWrapperStyles}>
        <Box sx={contentCenterStyles}>
          <Grow in timeout={300}>
            <Card elevation={0} sx={loginCardStyles}>
              <CardContent sx={loginCardContentStyles}>
                <Fade in timeout={500}>
                  <Box>
                    <LoginLogo alt={tenantName} logoUrl={tenantBranding.logoUrl} sx={logoStyles} />
                  </Box>
                </Fade>

                <Typography sx={loginHeaderStyles} variant="bold20">
                  {loginHeader}
                </Typography>

                {renderLoginContent()}
              </CardContent>
            </Card>
          </Grow>
        </Box>
      </Box>

      <Box sx={footerWrapperStyles}>
        <BrandingFooter isOverlay />
        <AppVersionInline color="common.white" />
      </Box>
    </Box>
  );
};

Login.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(Login);
